import { useCallback } from "react";

import { Client } from "@/Common/Api/Client";
import { ExternalCommunicationApiSearchPatientContactResponse } from "@/Common/Api/ExternalCommunications/Dto/ExternalCommunicationApiSearchPatientContactResponse";
import { TransferablePatientInfoResponse } from "@/Common/Api/ExternalCommunications/Dto/TransferablePatientInfoResponse";
import { Modal } from "@/Common/Components/Modals/Modal";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PatientsFoundResult } from "./PatientsFoundResult";
import { XcomPatientBanner } from "./XcomPatientBanner";

import styles from "./PatientImportDialog.module.scss";

interface IPatientImportDialogProps {
    model: IPatientBannerModel;
    onClose: () => void;
    onBack: () => void;
    onSelected: (personInfo: Patient) => void;
    clientTag: string;
    patient: Patient;
    claimId: string;
}

function PatientImportDialog(props: IPatientImportDialogProps): JSX.Element {
    const [patientsFound, setPatientsFound] = React.useState<TransferablePatientInfoResponse[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [selectedPatient, setSelectedPatient] = React.useState<TransferablePatientInfoResponse>(null);

    const footer = React.useMemo(() => (
        <div className="display-flex-justified-end">
            <div className="margin-xs-left">
                <button className="btn btn-default" onClick={() => props.onClose()}>
                    {cacheStore.GetString("CancelLabel")}
                </button>
            </div>
            <div className="margin-xs-left">
                <button className="btn btn-default" onClick={() => props.onBack()}>
                    {cacheStore.GetString("BackLabel")}
                </button>
            </div>
            <div className="margin-xs-left">
                <button
                    className="btn btn-primary"
                    disabled={!selectedPatient}
                    onClick={() => props.onSelected(Patient.fromTransferablePatientInfoResponse(selectedPatient))}
                >
                    {cacheStore.GetString("SearchLabel")}
                </button>
            </div>
        </div>
    ), [props, selectedPatient]);

    const updatePatientsFound = useCallback(async () => {
        const patientInfo = props.model.get("Model").IndividualResponse;
        const request = {
            ClientTag: props.clientTag,
            HealthInsuranceNumber: patientInfo.NAM,
            FirstName: patientInfo.FirstName,
            LastName: patientInfo.LastName,
            DateOfBirth: patientInfo.DateOfBirth,
            claimId: props.claimId
        };
        const response = await Client.postAsync<ExternalCommunicationApiSearchPatientContactResponse>(ServerOperations.Server.Myle_ExternalCommunication_SearchPatient, request);
        if (response.isSuccess && response.data) {
            setPatientsFound(response.data.Patients);
            setSelectedPatient(response.data.Patients?.firstOrDefault());
        }
        setIsLoading(false);
    }, [props.clientTag, props.claimId, props.model]);

    React.useEffect(() => {
        updatePatientsFound();
    }, [props, updatePatientsFound]);

    return (
        <Modal
            showOverlay
            fixedOverlayPosition
            showX
            centerContent
            width={1000}
            title={cacheStore.GetString("SearchForAPatientFileLabel")}
            onClose={props.onClose}
            footer={footer}
            disableOverlayClose={true}
        >
            <div className={styles.body}>
                <XcomPatientBanner patient={props.patient} />
                <PatientsFoundResult
                    isLoading={isLoading}
                    patients={patientsFound}
                    originalPatient={props.patient}
                    selectedPatient={selectedPatient}
                    onPatientSelected={p => setSelectedPatient(p)}
                />
            </div>
        </Modal>
    );
}

export { PatientImportDialog };
